import { addMonths, format, startOfMonth } from 'date-fns'
import { createSelector } from 'reselect'

import { EMPTY_OBJECT } from 'utils/emptyConstants'

import {
  selectActualBillableAgent,
  selectActualFeatureFlags,
  selectActualHasLimitedPocView,
  selectActualLicenses,
  selectActualOwnerStatus,
  selectActualPermissions,
  selectActualTimezone,
  selectIsBillingBypassed,
  selectIsMaster,
  selectIsPovTenant,
  selectNotifications,
  selectSelfCompanyId,
} from 'containers/App/selectors'
import { selectIsIncidentManagementCompany } from 'containers/Partner/selectors'
import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'

import { TENANT_TYPE } from './constants'
import { BILLING_MODELS, CREDIT_EXHAUSTION_THRESHOLDS } from './creditConstants'

export const selectIsPOV = createSelector(
  selectActualHasLimitedPocView,
  selectActualLicenses,
  (hasLimitedPocView, licenses) => Boolean(hasLimitedPocView || licenses?.mode?.poc)
)

/**
 * No money surface applies to this tenant, for any of three reasons:
 *   - POV_COSTUMER_FF — POV tenants get unlimited credits and are never billed
 *   - BILLING_BYPASS  — CM meters the tenant count-only (see selectIsBillingBypassed)
 *   - DISABLE_PAYG_FF — pay-as-you-go is switched off for the tenant
 *
 * Everything that shows a price, a payment method, a purchase CTA, an invoice or
 * a spend limit must be gated on this. Usage/metering surfaces (Overview counts,
 * History Log, the SOC Agent credit counter) stay visible — credits are still
 * counted under a bypass, they are just not charged.
 */
export const selectIsBillingDisabled = createSelector(
  [selectIsPovTenant, selectIsBillingBypassed, selectActualFeatureFlags],
  (isPovTenant, isBillingBypassed, ff) =>
    Boolean(isPovTenant || isBillingBypassed || ff?.DISABLE_PAYG_FF)
)

/**
 * The AI Credits settings page renders a single "unlimited, unbilled" POV
 * surface for both real POV tenants and billing-bypassed tenants — CM meters a
 * bypassed tenant count-only, so it gets the POV state, not whatever plan its
 * tenantType would otherwise suggest.
 */
export const selectShowsPovState = createSelector(
  [selectIsPovTenant, selectIsBillingBypassed],
  (isPovTenant, isBillingBypassed) => isPovTenant || isBillingBypassed
)

export const selectIsTenantOwner = selectActualOwnerStatus

// Uses selectActual* for both admin and owner status — an MSP partner's own
// (self) permissions don't apply when they're scoped into a child tenant via
// the company filter. Mixing selectIsAdmin (self) with selectActualOwnerStatus
// (actual) caused the credit CTAs to fall back to the non-admin variant for
// partner admins viewing a tenant they don't personally hold self-admin on
// (IC-49433).
export const selectIsAdminOrOwner = createSelector(
  selectActualPermissions,
  selectIsTenantOwner,
  (permissions, isOwner) => Boolean(permissions?.admin || isOwner)
)

export const selectTenantTimezone = selectActualTimezone

// ---------------------------------------------------------------------------
// billing/status (GET /members-api/creditmanagement/billing/status/) is the
// SINGLE source for the credit balance. getBillingStatusEpic loads it on app
// start (and again when an incident opens), so it is reachable outside /settings.
// ---------------------------------------------------------------------------

export const selectBillingStatus = (state) => state.agents.billingStatus
export const selectBillingStatusLoader = (state) => state.agents.loading.billingStatus
export const selectBillingStatusError = (state) => state.agents.error.billingStatus

// Normalize casing: the backend enum is lower-case, but responses may arrive
// upper-cased. Lowercasing keeps comparisons against TENANT_TYPE/PLAN_TYPE sound.
export const selectTenantType = (state) =>
  selectBillingStatus(state)?.tenantType?.toLowerCase() ?? null

/**
 * The effective billing model for the current tenant context, derived from
 * billing/status `tenantType`. POV_COSTUMER_FF (`selectIsPovTenant`) always wins
 * over that field. A trial/POC license (`selectIsPOV`) does NOT: a tenant that CM
 * reports as payg/prepurchase has activated real billing and is no longer POV,
 * whatever its monolith license still says (IC-51299). The license only decides
 * when CM reports 'pov', 'plg' or nothing.
 *
 * Stub-safe: falls back to PLG while the balance is unknown (loading, 404, or no
 * tenant record) so nothing downstream treats "unknown" as a paid plan.
 */
export const selectCreditsBillingModel = createSelector(
  [selectTenantType, selectIsPOV, selectIsPovTenant],
  (tenantType, isPOV, isPovTenant) => {
    if (isPovTenant) return BILLING_MODELS.POV
    if (tenantType === TENANT_TYPE.PAYG) return BILLING_MODELS.PAY_AS_YOU_GO
    if (tenantType === TENANT_TYPE.PREPURCHASE) return BILLING_MODELS.PREPURCHASE
    if (isPOV || tenantType === TENANT_TYPE.POV) return BILLING_MODELS.POV
    return BILLING_MODELS.PLG
  }
)

export const selectIsPaidBillingModel = createSelector(
  selectCreditsBillingModel,
  (model) => model === BILLING_MODELS.PAY_AS_YOU_GO || model === BILLING_MODELS.PREPURCHASE
)

/**
 * The tenant's credits are effectively uncapped, so nothing may block on a
 * balance and no "used/total" counter is meaningful.
 *
 * A billing-bypassed tenant qualifies: CM still counts its usage, but never
 * charges for it, so running out of budget cannot happen to it.
 */
export const selectIsUnlimitedCredits = createSelector(
  [selectCreditsBillingModel, selectIsBillingBypassed],
  (model, isBillingBypassed) => isBillingBypassed || model === BILLING_MODELS.POV
)

/**
 * A billing/status credit bucket (`{total, used, remaining}`) reduced to a
 * remaining count — or null when the bucket carries no allocation at all.
 *
 * The endpoint ZEROES unallocated buckets rather than nulling them ("zeros when
 * no PLG allocation" / "zeros when no PAYG cap set"), so a bare `remaining === 0`
 * must not be read as exhausted: on an uncapped PAYG tenant it means unlimited.
 * Only a bucket with a real allocation (non-zero total) can reach 0 = exhausted.
 */
const bucketRemaining = (bucket) => {
  if (!bucket?.total) return null
  return bucket.remaining ?? null
}

// null or 0 is how an unset PAYG monthly cap arrives (the status endpoint
// zeroes unallocated buckets). That cap is unlimited, so the free allowance
// must not become the ceiling once it is spent.
const hasPaygMonthlyCap = (status) => {
  const total = status?.paygCredits?.total
  return total != null && total !== 0
}

/**
 * The buckets that make up this tenant's spendable balance. PLG tenants spend
 * their monthly preview allocation; paid tenants burn the free allowance first
 * and then their own PAYG/prepurchase credits.
 */
const relevantBuckets = (status, tenantType) => {
  if (!status) return []
  if (tenantType === TENANT_TYPE.PLG) return [status.plgCredits]
  return [status.freeCredits, status.paygCredits]
}

// null = unknown (not fetched, 404, or no tenant record) — callers must treat
// that as "do not block", never as zero.
export const selectCreditsRemaining = createSelector(
  [selectBillingStatus, selectTenantType],
  (status, tenantType) => {
    if (!status || !tenantType) return null
    if (tenantType === TENANT_TYPE.PAYG && !hasPaygMonthlyCap(status)) {
      const freeRemaining = bucketRemaining(status.freeCredits)
      if (!freeRemaining) return null
      return freeRemaining
    }
    const counts = relevantBuckets(status, tenantType)
      .map(bucketRemaining)
      .filter((n) => n !== null)
    if (!counts.length) return null
    return counts.reduce((sum, n) => sum + n, 0)
  }
)

export const selectCreditsUsed = createSelector(
  [selectBillingStatus, selectTenantType],
  (status, tenantType) =>
    relevantBuckets(status, tenantType).reduce((sum, bucket) => sum + (bucket?.used ?? 0), 0)
)

export const selectMonthlyCreditLimit = createSelector(
  [selectBillingStatus, selectTenantType],
  (status, tenantType) => {
    if (!status || !tenantType) return null
    if (tenantType === TENANT_TYPE.PAYG && !hasPaygMonthlyCap(status)) return null
    const totals = relevantBuckets(status, tenantType)
      .map((bucket) => bucket?.total ?? null)
      .filter((total) => Boolean(total))
    if (!totals.length) return null
    return totals.reduce((sum, total) => sum + total, 0)
  }
)

export const selectTotalCredits = createSelector(
  [selectCreditsRemaining, selectCreditsUsed, selectMonthlyCreditLimit],
  (remaining, used, limit) => limit ?? (remaining != null ? remaining + used : null)
)

export const selectCreditsUsageRatio = createSelector(
  [selectCreditsUsed, selectMonthlyCreditLimit],
  (used, limit) => {
    if (!limit || limit <= 0) return 0
    return Math.min(used / limit, 1)
  }
)

// A master session (including switch-to-owner) and the IM company itself are
// not blocked by the tenant balance. The stream 402 stays the block.
const selectIsStaffInvestigation = createSelector(
  [selectIsMaster, selectIsIncidentManagementCompany],
  (isMaster, isIncidentManagementCompany) => Boolean(isMaster || isIncidentManagementCompany)
)

// CNC can leave Phishing SOC Agent enabled (phishSoc) and free (billPhishSoc
// false). Those runs cost nothing on any plan, so a spent balance must not
// block them. An absent flag stays billed: account status defaults it to true,
// and an MSP child has no billableAgent field.
const selectIsPhishSocNonBillable = (state) =>
  selectActualBillableAgent(state)?.billPhishSoc === false

export const selectIsCreditsExhausted = createSelector(
  [
    selectIsUnlimitedCredits,
    selectIsStaffInvestigation,
    selectIsPhishSocNonBillable,
    selectCreditsRemaining,
    selectCreditsUsageRatio,
  ],
  (isUnlimited, isStaffInvestigation, isPhishSocNonBillable, remaining, ratio) => {
    if (isUnlimited || isStaffInvestigation || isPhishSocNonBillable) return false
    if (remaining === null) return false
    if (remaining === 0) return true
    return ratio >= CREDIT_EXHAUSTION_THRESHOLDS.EXHAUSTED
  }
)

export const selectIsCreditsApproachingLimit = createSelector(
  [
    selectIsUnlimitedCredits,
    selectIsStaffInvestigation,
    selectIsPhishSocNonBillable,
    selectCreditsUsageRatio,
    selectIsCreditsExhausted,
  ],
  (isUnlimited, isStaffInvestigation, isPhishSocNonBillable, ratio, isExhausted) => {
    if (isUnlimited || isStaffInvestigation || isPhishSocNonBillable || isExhausted) return false
    return ratio >= CREDIT_EXHAUSTION_THRESHOLDS.WARNING
  }
)

// PLG credits reset on the 1st at midnight Israel time; format in that zone, else
// the date rolls back a day for tenants west of Israel (PLG Experience §1).
const RESET_TIMEZONE = 'Asia/Jerusalem'

const ordinalSuffix = (day) => {
  const mod100 = day % 100
  if (mod100 >= 11 && mod100 <= 13) return 'th'
  switch (day % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

// Reset date format '{Mon DDth}', e.g. "Jul 1st" (PLG Experience §2.1).
const formatResetDate = (date) => {
  if (!date) return null
  if (typeof Intl !== 'undefined') {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: RESET_TIMEZONE,
        month: 'short',
        day: 'numeric',
      }).formatToParts(date)
      const month = parts.find((p) => p.type === 'month')?.value
      const day = parts.find((p) => p.type === 'day')?.value
      if (month && day) return `${month} ${day}${ordinalSuffix(Number(day))}`
    } catch (err) {
      console.warn('Failed to format reset date in Israel timezone, falling back to local:', err)
    }
  }
  return format(date, 'MMM do')
}

// Credits reset / next billing ALWAYS falls on the 1st of the next month at
// midnight Asia/Jerusalem (product rule, IC-49435). billing/status
// `nextBillingDate` is not timezone-correct, so the FE computes this for every
// plan — PLG, PAYG, prepurchase, POV — rather than formatting the backend
// field. Anchor "which month is it" to Israel, not the viewer's machine:
// around month boundaries a local-tz month is off by one for viewers on the
// other side of Israel's date line.
export const selectNextResetDate = () => {
  if (typeof Intl !== 'undefined') {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: RESET_TIMEZONE,
        year: 'numeric',
        month: 'numeric',
      }).formatToParts(new Date())
      const year = Number(parts.find((p) => p.type === 'year')?.value)
      const israelMonth = Number(parts.find((p) => p.type === 'month')?.value) // 1-12
      if (year && israelMonth) {
        // israelMonth is 1-based, Date.UTC months are 0-based — passing it as-is
        // IS next month (Dec rolls over to Jan). Noon UTC is unambiguously the
        // 1st in Israel (UTC+2/+3), so formatResetDate renders the right day.
        return new Date(Date.UTC(year, israelMonth, 1, 12))
      }
    } catch (err) {
      console.warn('Failed to compute reset date in Israel timezone, falling back to local:', err)
    }
  }
  return startOfMonth(addMonths(new Date(), 1))
}

export const selectNextResetLabel = createSelector([selectNextResetDate], (date) =>
  formatResetDate(date)
)

export const selectBundleExpirationDate = createSelector([selectBillingStatus], (status) => {
  if (!status?.bundleExpiresAt) return null
  const parsed = new Date(status.bundleExpiresAt)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed
})

export const selectBundleExpirationLabel = createSelector(
  [selectBundleExpirationDate, selectTenantTimezone],
  (date, tz) => {
    if (!date) return null
    if (tz && typeof Intl !== 'undefined') {
      try {
        return new Intl.DateTimeFormat('en-US', {
          timeZone: tz,
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        }).format(date)
      } catch (err) {
        console.warn(
          'Failed to format expiration with tenant timezone, falling back to local:',
          err
        )
      }
    }
    return format(date, 'MMMM do, yyyy')
  }
)

/**
 * Achievements derivations for the credit-request popup.
 * These are stub-safe: returns null when backend hasn't shipped them, which
 * tells the popup to hide the achievements block.
 *
 * PRD formulas (Purchase Flow §3.4):
 *   timeSavedHours    = 30 minutes × successful invocations / 60
 *   peopleUsingAI     = distinct users with ≥1 credit consumed this month
 *   tasksCompleted    = successful invocations this month
 */
export const selectCompanyAchievements = createSelector(
  [selectBillingStatus, selectTenantTimezone],
  (status, tz) => {
    const stats = status?.usageStats
    if (!stats) return null
    const successful = stats.successfulInvocationsThisMonth ?? 0
    const distinct = stats.distinctActiveUsersThisMonth ?? 0
    const computedTimeSaved = stats.timeSavedHours ?? +(successful * 0.5).toFixed(1)
    let monthName
    try {
      monthName = new Intl.DateTimeFormat('en-US', {
        timeZone: tz || undefined,
        month: 'long',
      }).format(new Date())
    } catch (err) {
      console.warn('Failed to format month name with tenant timezone, falling back to local:', err)
      monthName = format(new Date(), 'MMMM')
    }
    return {
      monthName,
      timeSavedHours: computedTimeSaved,
      peopleUsingAI: distinct,
      tasksCompleted: successful,
    }
  }
)

/**
 * Sub-tenant context: true when an MSP/parent is currently scoped into a
 * specific child tenant via the partner filter. Credit displays should use
 * the actual tenant's data while in this context.
 */
export const selectIsViewingSubTenant = createSelector(
  [selectCompanyFilter, selectSelfCompanyId],
  (filter, selfId) => {
    const filterId = filter?.value
    if (!filterId) return false
    if (filterId === selfId) return false
    return true
  }
)

/**
 * The full ai_credits notification slice as pushed by the backend on
 * `getStatus` (and on socket updates once the socket epic is wired in).
 *
 * Expected shape (stub-safe — returns undefined until backend ships):
 *   {
 *     type: 'plg_exhausted' | 'approaching_limit' | 'credit_limit_reached',
 *     billing_model: 'pay_as_you_go' | 'prepurchase',
 *     reset_date_label: string,
 *     expiration_date_label: string,
 *     pending_request: {
 *       request_id: string,         // rotates per new request, used for session dismissal
 *       requester_name: string,
 *       other_requesters_count: number,
 *     }
 *   }
 */
export const selectAiCreditsNotification = createSelector(
  [selectNotifications],
  (notifications) => notifications?.ai_credits ?? null
)

/**
 * Pending "Request more" credit request from analyst(s). Drives the admin
 * popup (PLG §3.2 / Purchase §3.4). Returns null when no request is pending.
 */
export const selectPendingCreditRequest = createSelector(
  [selectAiCreditsNotification],
  (notification) => notification?.pending_request ?? null
)

// ---------------------------------------------------------------------------
// Billing / status — the rest of the payload (payment method, plan, invoicing).
// The balance derivations live at the top of this file.
// ---------------------------------------------------------------------------

export const selectCardInfo = (state) => selectBillingStatus(state)?.cardInfo ?? null

/** PLG preview credits still available this cycle (null when not applicable). */
export const selectPlgRemaining = (state) =>
  selectBillingStatus(state)?.plgCredits?.remaining ?? null

/** PLG monthly preview credit allowance (null when not applicable). */
export const selectPlgTotal = (state) => selectBillingStatus(state)?.plgCredits?.total ?? null

/**
 * PLG tenants are on the free preview plan (not purchased).
 *
 * POV_COSTUMER_FF (and billing bypass, via selectShowsPovState) always wins
 * over tenantType: Overview stays on the unlimited/unbilled POV surface even
 * when CM reports 'plg' or null. Do not skip this check — a null tenantType
 * otherwise looks like unprovisioned PLG.
 *
 * A loaded billing/status with `tenantType: null` (and no POV/bypass) is an
 * unprovisioned CM tenant — Overview uses the same preview surface as PLG.
 * PaymentMethodAlert still must not infer planType PLG: the endpoint zeros
 * plgCredits and that would fire the exhausted / Upgrade banner.
 */
export const selectIsPlgPreview = createSelector(
  [selectTenantType, selectShowsPovState, selectBillingStatusLoader],
  (tenantType, showsPovState, loading) => {
    if (showsPovState) return false
    if (tenantType === TENANT_TYPE.PLG) return true
    return !tenantType && !loading
  }
)

/**
 * Whether the tenant is on a paid plan. Driven by `tenantType`, NOT by the
 * presence of a Stripe `customerId` — PLG tenants can have a customerId without
 * having purchased a plan.
 */
export const selectHasPurchased = (state) => {
  const loader = selectBillingStatusLoader(state)
  if (loader) return null
  const tenantType = selectTenantType(state)
  if (!tenantType) return false
  return tenantType !== TENANT_TYPE.PLG
}

/**
 * Self-serve Stripe cancel is only valid for a PAYG tenant with a card on file.
 * `tenantType` (via selectCreditsBillingModel) is the paid-plan check; cardInfo.last4
 * is the payment-method check. Prepurchase, PLG, and POV must not see Unsubscribe.
 */
export const selectCanCancelSubscription = createSelector(
  [selectCreditsBillingModel, selectCardInfo],
  (model, cardInfo) => model === BILLING_MODELS.PAY_AS_YOU_GO && Boolean(cardInfo?.last4)
)

/**
 * Derives the billable/free financial figures the overview cards and the
 * PaymentMethodAlert expect, mapped from the billing/status response.
 *   billableTotal/Used/Remaining — PAYG monthly credit cap and consumption
 *   billableLimitUsage — % of the monthly limit consumed (billable only); null
 *     when no monthly limitation is set
 *   plgRemaining — PLG preview credits still available this cycle
 *   freeUsed/freeTotal/freeRemaining — the free credits allowance
 */
export const selectCreditFinancials = createSelector(selectBillingStatus, (status) => {
  const payg = status?.paygCredits ?? null
  const plg = status?.plgCredits ?? null
  const freeCredits = status?.freeCredits ?? null
  // Match selectBillingSummary: null or 0 total means unlimited (0 is how the
  // user resets). Do not infer from limitUsage — it can stay non-null after a
  // limit is cleared and would incorrectly show the usage bar / "out of X".
  const monthlyLimit = payg?.total ?? null
  const hasMonthlyLimitation = monthlyLimit != null && monthlyLimit !== 0
  const billableLimitUsage = hasMonthlyLimitation ? (payg?.limitUsage ?? null) : null
  return {
    hasMonthlyLimitation,
    billableTotal: hasMonthlyLimitation ? monthlyLimit : 0,
    billableUsed: payg?.used ?? 0,
    billableRemaining: payg?.remaining ?? 0,
    billableLimitUsage,
    plgRemaining: plg?.remaining ?? null,
    freeUsed: freeCredits?.used ?? 0,
    freeTotal: freeCredits?.total ?? 0,
    freeRemaining: freeCredits?.remaining ?? 0,
  }
})

/**
 * Next billing / reset label ("Mon DDth", e.g. "Aug 1st").
 *
 * Always the FE-computed 1st of next month in Asia/Jerusalem
 * (`selectNextResetLabel`). Do not read billing/status `nextBillingDate` —
 * its timezone is wrong. One source of truth for Overview, Billing summary,
 * exhausted screens, and credit-request toasts.
 */
export const selectNextBillingLabel = selectNextResetLabel

/**
 * Maps the billing/status response to the BillingSummaryCard rows.
 * Fields without a source in billing/status are left unset so the card renders
 * them as '—'. `freeCredits` is the formatted label; `freeCreditsValue` is the
 * raw allowance (the card hides the row when that is not > 0).
 */
export const selectBillingSummary = createSelector(
  selectBillingStatus,
  selectNextBillingLabel,
  (status, nextBilling) => {
    if (!status) return EMPTY_OBJECT
    const monthlyLimit = status.paygCredits?.total ?? null
    const freeTotal = status.freeCredits?.total
    // A limit of null or 0 means unlimited (0 is how the user resets to unlimited).
    const hasLimit = monthlyLimit != null && monthlyLimit !== 0
    return {
      nextBilling,
      freeCredits: freeTotal != null ? `${freeTotal.toLocaleString('en-US')} Credits` : null,
      freeCreditsValue: freeTotal ?? null,
      monthlyLimitation: hasLimit ? `${monthlyLimit.toLocaleString('en-US')} Credits` : 'Unlimited',
      // Raw value backing the inline editor (null/0 → unlimited).
      monthlyLimitationValue: hasLimit ? monthlyLimit : null,
    }
  }
)
